import { useContext, useEffect, useRef, useState } from 'react';
import { Box, useToast } from '@chakra-ui/react';
import {
  ChatMessage,
  ChatMessageType,
} from '@reclaim-developer-experience/dev-tool-client-sdk/dist';
import { getAuth } from 'firebase/auth';
import { app } from '../../configs/firebaseApp';
import { AmplitudeAnalyticsContext } from '../../contexts/useAnalytics';
import { getWebClient } from '../../lib/client';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setCurrentUrlInput,
  updateAppConnected,
} from '../../redux/providerBuilder/mobileClient';
import {
  getAppConnected,
  getCurrentUrlInput,
} from '../../redux/providerBuilder/mobileClient/selectors';
import {
  updateNetworkStore,
  updatePagesVisted,
} from '../../redux/providerBuilder/networkRequestStore';
import {
  getAllNetworkRequest,
  getPagesVisted,
} from '../../redux/providerBuilder/networkRequestStore/selectors';
import { getActiveStep } from '../../redux/providerBuilder/stepperDetails/selectors';
import { generateSession } from '../../redux/session/actions';
import { getSessionId } from '../../redux/session/selectors';
import { getFirebaseIdToken } from '../../utils/utils';
import { InitialiseSession } from './components/initialise-session/initialise-session.component';
import { StepperForm } from './components/provider-details/stepper-form.component';
import { RequestSelection } from './components/request-selection/request-selection.component';
import { ReviewProvider } from './components/test-provider/review-provider.component';
import { useLocation } from 'react-router-dom';
import { useSidebar } from 'src/components/ui/sidebar';

export const ProviderBuilderV3Screen = () => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector(getSessionId);
  const networkRequests = useAppSelector(getAllNetworkRequest);
  const pagesVisited = useAppSelector(getPagesVisted);
  const activeStep = useAppSelector(getActiveStep);
  const appConnected = useAppSelector(getAppConnected);
  const currentUrlInput = useAppSelector(getCurrentUrlInput);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get('isEdit');

  const auth = getAuth(app);

  const [accessToken, setAccessToken] = useState('');
  const [allPagesVisited, setAllPagesVisited] = useState<string[]>([]);
  const sessionInit = useRef(false);

  const { logEvent } = useContext(AmplitudeAnalyticsContext);

  const { toggleSidebar } = useSidebar();


  useEffect(() => {
    if (!isEdit) {
      generateSessionHandler();
    }
    refreshAccessToken();
  }, [accessToken, isEdit]);

  useEffect(() => {
    toggleSidebar();
  }, []);

  useEffect(() => {
    if (sessionId && !isEdit) {
      const interval = setInterval(() => {
        receiveMessages();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [sessionId, pagesVisited, isEdit]);

  const generateSessionHandler = async () => {
    if (!sessionInit.current) {
      sessionInit.current = true;
      await dispatch(generateSession({ accessToken }));
    }
  };

  async function refreshAccessToken() {
    const newToken = await getFirebaseIdToken();
    if (newToken !== accessToken) {
      setAccessToken(newToken as string);
    }
    return newToken;
  }

  function makeid(length: number) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }

  const newHandler = (message: ChatMessage, networkRequestCount: number = 0) => {
    if (message.type !== ChatMessageType.CONNECTION_PENDING) {
      dispatch(updateAppConnected(true));
    }

    if (message.type === ChatMessageType.CONNECTED) {
      dispatch(updateAppConnected(true));
      logEvent('DEVTOOL_MOBILE_CLIENT_CONNECTED');
      toast({
        title: 'Mobile Client Connected Successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }

    if (message.type === ChatMessageType.M_RECLAIM_URL_RECEIVED) {
      dispatch(setCurrentUrlInput(message.message));
    }

    if (message.type === ChatMessageType.M_NETWORK_REQUESTS_AND_RESPONSES) {
      const data = JSON.parse(message.message);
      data.id = makeid(5);
      data.intId = networkRequests.length;
      data.countId = Date.now() + networkRequestCount;

      if (data.request?.headers && typeof data.request.headers === 'object') {
        data.request.headers = Object.entries(data.request.headers).map(([key, value]) => ({
          key,
          value: String(value),
        }));
      }

      if (data.cookies) {
        data.cookies = Object.values(data.cookies);
      }

      if (data.currentPageUrl && currentUrlInput !== data.currentPageUrl) {
        dispatch(setCurrentUrlInput(data.currentPageUrl));
      }

      if (!allPagesVisited.includes(data.currentPageUrl)) {
        setAllPagesVisited([...allPagesVisited, data.currentPageUrl]);
        dispatch(updatePagesVisted(data.currentPageUrl));
      }

      dispatch(updateNetworkStore(data));
    }
  };

  const receiveMessages = async () => {

    const newAccessToken = await refreshAccessToken();
    const client = getWebClient(newAccessToken as string);
    try {
      let networkRequestCount = 0;
      const response = await client.receiveMessage(sessionId);
      if (response.length) {
        
        response.forEach((message: ChatMessage, index: number) => {
          networkRequestCount += 1;
          try {
            newHandler(message, networkRequestCount);
          } catch (e: unknown) {
            console.error(`[Error] Failed to process message ${index + 1}/${response.length}:`, e);
            console.log('[Debug] Problematic message:', JSON.stringify(message, null, 2));
          }
        });

      }

      if (appConnected && newAccessToken !== accessToken) {
        client.sendMessage(
          {
            message: newAccessToken as string,
            type: ChatMessageType.W_SET_NEW_ACCESS_TOKEN,
          },
          sessionId
        );
      }
    } catch (e) {
      console.error('[Error] Message receiving failed:', e);
    } 
  };


  return (
      <Box w="100%" maxW="1400px" mx="auto" py={3}>
        {activeStep === 0 && (
          <InitialiseSession
            accessToken={accessToken}
            userUid={auth.currentUser?.uid as string}
          />
        )}
        {activeStep === 1 && (
          <RequestSelection
            accessToken={accessToken}
          />
        )}
        {activeStep === 2 && (
          <StepperForm
          />
        )}
        {activeStep === 3 && <ReviewProvider />}
      </Box>
  );
};
